import { createSelector } from 'reselect'
import { OrderFeedbackGuestExperienceState } from './types'

export const selectOrderFeedbackGuestExperienceState = (state: {
  orderFeedbackGuestExperience: OrderFeedbackGuestExperienceState
}) => state.orderFeedbackGuestExperience

// `selectFormDisabled` gates the pane's CONTROLS, and "a save is in flight" is
// the only reason to disable those. Publish has further conditions of its
// own, see `selectPublishDisabled`.
export const selectFormDisabled = createSelector(
  [selectOrderFeedbackGuestExperienceState],
  state => state.isFormSubmitting
)

export const selectCallTheStoreEnabled = createSelector(
  [selectOrderFeedbackGuestExperienceState],
  state => state.callTheStoreEnabled
)

export const selectAcknowledgmentMessage = createSelector(
  [selectOrderFeedbackGuestExperienceState],
  state => state.acknowledgmentMessage
)

// The last call-the-store/acknowledgment pair known to be SAVED, as opposed to
// the pair being edited. No consumer needs these yet (there is no "current
// setup" card on this pane), but they complete the pair the way
// selectPersistedAllowWrittenFeedbackCollection does for the sibling slice.
export const selectPersistedCallTheStoreEnabled = createSelector(
  [selectOrderFeedbackGuestExperienceState],
  state => state.persistedCallTheStoreEnabled
)

export const selectPersistedAcknowledgmentMessage = createSelector(
  [selectOrderFeedbackGuestExperienceState],
  state => state.persistedAcknowledgmentMessage
)

export const selectIsFormSubmitting = createSelector(
  [selectOrderFeedbackGuestExperienceState],
  state => state.isFormSubmitting
)

// FR-57's backend validation bound (nexus thanx/nexus#1977). The textarea's
// own `maxLength` prop already stops a merchant from typing past this, but a
// value can still arrive over the limit from before this cap existed (or from
// a bypassed control), so Publish must check it independently rather than
// trust the input alone.
export const MAX_ACKNOWLEDGMENT_LENGTH = 500

export const selectAcknowledgmentTooLong = createSelector(
  [selectAcknowledgmentMessage],
  message => message.length > MAX_ACKNOWLEDGMENT_LENGTH
)

// True when either edited value differs from its persisted counterpart.
export const selectIsFormDirty = createSelector(
  [selectOrderFeedbackGuestExperienceState],
  state =>
    state.callTheStoreEnabled !== state.persistedCallTheStoreEnabled ||
    state.acknowledgmentMessage !== state.persistedAcknowledgmentMessage
)

// Publish is disabled while a save is in flight, whenever there is nothing to
// save, OR while the acknowledgment message is over the backend's limit — a
// merchant must not be able to publish a message the server would reject.
export const selectPublishDisabled = createSelector(
  [selectFormDisabled, selectIsFormDirty, selectAcknowledgmentTooLong],
  (formDisabled, isFormDirty, tooLong) =>
    formDisabled || !isFormDirty || tooLong
)

// The PATCH body carries exactly these two keys.
export const selectApiFormattedFormData = createSelector(
  [selectOrderFeedbackGuestExperienceState],
  state => ({
    order_feedback_call_the_store_enabled: state.callTheStoreEnabled,
    order_feedback_acknowledgment_message: state.acknowledgmentMessage,
  })
)
